import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../login/AuthProvider";
import { backend } from '../../components/apipath';
import { ColGroup } from "../../components/ColGroup.js";

function ReferralRequest() {
  // 這個元件現在是「推薦後台」用
  const { token } = useAuth();
  const [requestList, setRequestList] = useState([]);
  const [loading, setLoading] = useState(true);

  const limit = 50;
  const [page, setPage] = useState(1);
  const [rowsCount, setRowsCount] = useState(0);

  // 搜尋條件：關鍵字 + 建立日期區間
  const [search, setSearch] = useState({
    keyword: "",
    start: "",
    end: "",
  });

  // 備註編輯
  const [editingId, setEditingId] = useState(null);
  const [memoEdits, setMemoEdits] = useState({});

  // 取得推薦資料
  const fetchPageData = async (targetPage = 1) => {
    try {
      setLoading(true);

      const params = {
        page: targetPage,
        limit,
        keyword: search.keyword || "",
        startDate: search.start || "",
        endDate: search.end || "",
      };

      const res = await axios.get(`${backend}referral/list`, {
        headers: { token },
        params,
        withCredentials: true,
      });

      if (res.data?.success) {
        const list = res.data.data || [];
        setRequestList(list);
        setRowsCount(res.data.total || 0);
        setPage(targetPage);

        // 初始化備註
        const initMemos = {};
        list.forEach((row) => {
          initMemos[row.id] = row.admin_memo || "";
        });
        setMemoEdits(initMemos);
      } else {
        setRequestList([]);
        setRowsCount(0);
      }
    } catch (err) {
      console.error("❌ 取得推薦列表失敗:", err);
      setRequestList([]);
      setRowsCount(0);
    } finally {
      setLoading(false);
    }
  };

  // 第一次載入
  useEffect(() => {
    fetchPageData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 搜尋
  const handleSearch = () => {
    fetchPageData(1);
  };

  // 分頁
  const changePage = (newPage) => {
    const totalPages = Math.ceil(rowsCount / limit) || 1;
    if (newPage < 1 || newPage > totalPages) return;
    fetchPageData(newPage);
  };

  // 日期格式 YYYY-MM-DD
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // 開始編輯備註
  const startEditing = (row) => {
    setEditingId(row.id);
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  // 備註變更
  const handleMemoChange = (id, value) => {
    setMemoEdits((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // 儲存備註
  const saveChanges = async () => {
    if (!editingId) return;
    const memo = memoEdits[editingId] ?? "";

    try {
      await axios.put(
        `${backend}referral/${editingId}/memo`,
        { admin_memo: memo },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            token: token,
          },
          withCredentials: true,
        }
      );

      // 更新前端資料
      setRequestList((prev) =>
        prev.map((row) =>
          row.id === editingId ? { ...row, admin_memo: memo } : row
        )
      );

      setEditingId(null);
    } catch (error) {
      console.error("❌ 更新備註失敗:", error);
    }
  };

  // 欄位比例：10 欄
  const colWeights = [1, 1, 1, 1, 1, 1, 1, 2, 2, 1];
  const totalPages = Math.ceil(rowsCount / limit) || 1;

  return (
    <>
      <div className="mt100 container-fluid">
        {loading ? (
          <p>載入中...</p>
        ) : (
          <div className="mt-2">
            {/* 搜尋列 */}
            <div className="row mb-3">
              <div className="col-md-3 mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="學員姓名 / 電話 / 帳號"
                  value={search.keyword}
                  onChange={(e) =>
                    setSearch({ ...search, keyword: e.target.value })
                  }
                />
              </div>

              <div className="col-md-2 mb-2">
                <input
                  type="date"
                  className="form-control"
                  value={search.start}
                  onChange={(e) =>
                    setSearch({ ...search, start: e.target.value })
                  }
                />
              </div>

              <div className="col-md-2 mb-2">
                <input
                  type="date"
                  className="form-control"
                  value={search.end}
                  onChange={(e) =>
                    setSearch({ ...search, end: e.target.value })
                  }
                />
              </div>

              <div className="col-md-2 mb-2">
                <button className="btn btn-primary w-100" onClick={handleSearch}>
                  搜尋
                </button>
              </div>
            </div>

            {/* 列表 */}
            <table className="table table-striped border table-fixed w-100">
              <ColGroup weights={colWeights} />
              <thead>
                <tr>
                  <th>建立日期</th>
                  <th>學員姓名</th>
                  <th>連絡電話</th>
                  <th>是否曾購買方案</th>
                  <th>收款銀行</th>
                  <th>分行名稱</th>
                  <th>銀行帳號</th>
                  <th>地址</th>
                  <th>備註</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {requestList.map((row) => (
                  <tr key={row.id}>
                    {editingId === row.id ? (
                      <>
                        {/* 編輯模式：把備註展開在一個大欄位 */}
                        <td>{formatDate(row.created_at)}</td>
                        <td>
                          {row.mem_id ? (
                            <a
                              href={`/adm/admmemberedit/${row.mem_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {row.referrer_name}
                            </a>
                          ) : (
                            row.referrer_name
                          )}
                        </td>
                        <td>{row.contact_phone}</td>
                        <td>
                          {Number(row.has_purchased) === 1 ? "是" : "否"}
                        </td>
                        <td>{row.bank_name}</td>
                        <td>{row.bank_branch}</td>
                        <td>{row.bank_account}</td>
                        <td>{row.address}</td>
                        <td>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">備註</span>
                            <textarea
                              className="form-control"
                              rows={3}
                              value={memoEdits[row.id] ?? ""}
                              onChange={(e) =>
                                handleMemoChange(row.id, e.target.value)
                              }
                              placeholder="內部備註（不顯示給學生）"
                            />
                          </div>
                        </td>
                        <td>
                          <button
                            className="btn btn-primary btn-sm m-1"
                            onClick={saveChanges}
                          >
                            保存
                          </button>
                          <button
                            className="btn btn-secondary btn-sm m-1"
                            onClick={cancelEditing}
                          >
                            取消
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{formatDate(row.created_at)}</td>
                        <td>
                          {row.mem_id ? (
                            <a
                              href={`/adm/admmemberedit/${row.mem_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {row.referrer_name}
                            </a>
                          ) : (
                            row.referrer_name
                          )}
                        </td>
                        <td>{row.contact_phone}</td>
                        <td>
                          {Number(row.has_purchased) === 1 ? "是" : "否"}
                        </td>
                        <td>{row.bank_name}</td>
                        <td>{row.bank_branch}</td>
                        <td>{row.bank_account}</td>
                        <td>{row.address}</td>
                        <td>{row.admin_memo}</td>
                        <td>
                          <button
                            className="btn btn-primary btn-sm m-1"
                            onClick={() => startEditing(row)}
                          >
                            編輯
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}

                {requestList.length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center">
                      目前沒有資料
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* 分頁 */}
            <div className="mt-3 text-center">
              <button
                className="btn btn-outline-secondary me-2"
                disabled={page === 1}
                onClick={() => changePage(page - 1)}
              >
                上一頁
              </button>
              <span>
                第 {page} 頁 / 共 {totalPages} 頁（共 {rowsCount} 筆）
              </span>
              <button
                className="btn btn-outline-secondary ms-2"
                disabled={page >= totalPages}
                onClick={() => changePage(page + 1)}
              >
                下一頁
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ReferralRequest;
